import { useEffect, useRef, useState, type ReactNode } from "react";
import { animate, LazyMotion, MotionConfig, useReducedMotion, type Transition } from "motion/react";

/**
 * Motion for the product pages.
 *
 * CSS (web/src/motion.css) handles what CSS can: entrances, hovers, presses.
 * This library is used only for what it cannot do: animating an element *out*
 * as it is removed, letting a grid reflow smoothly around the gap, and a tab
 * highlight that travels between tabs. Components use `m.*` (strict
 * LazyMotion), never `motion.*`, so the features stay in their lazy chunk.
 *
 * `reducedMotion="user"` makes every Motion animation honour the OS setting,
 * matching the CSS kill switch in styles.css.
 */
export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <MotionConfig reducedMotion="user">
      <LazyMotion features={() => import("./motion-features.js").then((mod) => mod.default)} strict>
        {children}
      </LazyMotion>
    </MotionConfig>
  );
}

/** Springs, named for how they feel. */
export const springs = {
  /** Tabs, chips: arrives fast, no wobble. */
  snappy: { type: "spring", stiffness: 520, damping: 40, mass: 0.8 } satisfies Transition,
  /** Cards and list rows reflowing. */
  soft: { type: "spring", stiffness: 300, damping: 30 } satisfies Transition,
  /** A celebration: a little overshoot. */
  bouncy: { type: "spring", stiffness: 380, damping: 18 } satisfies Transition,
} as const;

/**
 * Enter and leave for one item in a list or grid. The stagger is capped so a
 * forty-photo grid does not take two seconds to finish arriving.
 */
export function listItem(index: number) {
  return {
    initial: { opacity: 0, y: 12, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1, transition: { ...springs.soft, delay: Math.min(index, 8) * 0.035 } },
    exit: { opacity: 0, scale: 0.92, transition: { duration: 0.18 } },
  } as const;
}

/**
 * A number that counts up to its value when it first appears, and glides to a
 * new value when it changes. With reduced motion it is simply the number.
 */
export function CountUp({ value, format = (n) => String(Math.round(n)) }: { value: number; format?: (n: number) => string }) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? value : 0);
  const from = useRef(reduce ? value : 0);

  useEffect(() => {
    if (reduce) {
      setShown(value);
      return;
    }
    const controls = animate(from.current, value, {
      duration: Math.min(1.2, 0.4 + Math.log10(Math.max(1, Math.abs(value))) * 0.25),
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => {
        from.current = latest;
        setShown(latest);
      },
    });
    return () => controls.stop();
  }, [value, reduce]);

  return <>{format(shown)}</>;
}
